/**
 * Who you are, and the dice that changes it.
 *
 * There is no text field here and there is not one anywhere else either. A name is rolled from
 * vocabulary this build owns, which is what makes it safe to put on three strangers' screens
 * without a moderation story, and pressing the dice is the whole of the interaction.
 *
 * The same row serves the menu and the lobby. On the menu it decides what this machine will be
 * called when it opens a session; in the lobby it renames a seat that already exists. Both are one
 * callback with a name in it, which is why this is a component rather than two similar rows.
 *
 * The name is rolled here rather than handed in, because the roller owns the dice and the caller
 * owns what a new name means. The caller is told the result and never asked for one.
 */

import { NAME_ROLLER_ACTION, NAME_ROLLER_HINT, NAME_ROLLER_LABEL } from '@/text/identityText';
import { el, setText } from '@/ui/dom';
import { injectNameRollerStyles } from '@/ui/identity/nameRollerStyles';
import { rollPlayerName } from '@/identity/rollPlayerName';

/** A die: it reads at 20px, and it is the only thing on the row worth pressing. */
const PIP = String.fromCodePoint(0x1f3b2);

export interface NameRollerHandlers {
  /** The name showing when the row is built. */
  readonly name: string;
  /** A fresh name, rolled here. Called on every press, never on the first render. */
  readonly onRoll: (name: string) => void;
}

export interface NameRoller {
  readonly root: HTMLElement;
  /**
   * The dice, exposed so a caller can lay it out somewhere other than beside the name.
   *
   * The menu puts it at the head of a row of four buttons, because on that screen rerolling is one
   * of the things you can do to a character rather than a property of the row saying who you are.
   * The lobby leaves it where it is built. Moving it is an `append` on the caller's side, and the
   * roller neither knows nor cares which happened.
   */
  readonly dice: HTMLButtonElement;
  /** Follows a name that changed elsewhere, so two rollers never disagree about one player. */
  show(name: string): void;
  dispose(): void;
}

export function createNameRoller(handlers: NameRollerHandlers): NameRoller {
  const removeStyles = injectNameRollerStyles();

  const root = el('section', 'name-roller');
  const name = el('span', 'name-roller-name', handlers.name);

  const dice = el('button', 'name-roller-dice');
  dice.type = 'button';
  dice.setAttribute('aria-label', NAME_ROLLER_HINT);
  dice.append(el('span', 'name-roller-pip', PIP), el('span', '', NAME_ROLLER_ACTION));

  dice.addEventListener('click', () => {
    const rolled = rollPlayerName();
    setText(name, rolled);
    handlers.onRoll(rolled);
  });

  // Announced rather than silent: the name changing is the only thing this button does, and a
  // screen reader that is not told has no way to hear the dice land.
  name.setAttribute('role', 'status');

  root.append(el('span', 'name-roller-label', NAME_ROLLER_LABEL), name, dice);

  return {
    root,
    dice,
    show(next: string): void {
      setText(name, next);
    },
    dispose(): void {
      // Both, because the dice may have been moved out of the row by whoever mounted it.
      dice.remove();
      root.remove();
      removeStyles();
    },
  };
}
